import { useEffect, useState, useRef, ReactNode } from 'react';
import RoundedButton from "components/RoundedButton";
import { observer } from 'mobx-react-lite';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { colors } from 'utils/constants';
import { useInterval } from 'utils/useInterval';
import { getCta } from 'utils/common';

interface INextWinnerBannerProps {
  newStyle?: boolean;
  darkMode?: boolean;
  className?: string;
  fixedToBottom?: boolean;
  showDays?: boolean;
  showSeconds?: boolean;
  heading?: ReactNode;
  barTint?: string;
  withEnterDrawButton?: boolean;
  showUpcomingDrawDate?: boolean;
  style?: React.CSSProperties;
}

const NextWinnerBanner = observer(
  ({
    newStyle = true,
    darkMode = false,
    className,
    fixedToBottom = false,
    showDays = true,
    showSeconds = true,
    heading = (
      <>
        MORE <strong>WINNERS</strong> IN
      </>
    ),
    barTint = '#C7F4EC',
    withEnterDrawButton = false,
    showUpcomingDrawDate = true,
    style,
  }: INextWinnerBannerProps) => {
    const { upcomingDrawDate } = useDrawStore();

    const MILLIS_IN_DAY = 1000 * 60 * 60 * 24;
    const MILLIS_IN_HOUR = 1000 * 60 * 60;
    const MILLIS_IN_MINUTE = 1000 * 60;

    const [days, setDays] = useState<number | null>(null);
    const [hours, setHours] = useState<number | null>(null);
    const [mins, setMins] = useState<number | null>(null);
    const [secs, setSecs] = useState<number | null>(null);

    const updateCountdownComponents = () => {
      if (!upcomingDrawDate || !showUpcomingDrawDate) {
        return;
      }

      let now: number;
      let t: number;

      now = new Date().getTime();
      t = upcomingDrawDate.getTime() - now;

      if (t >= 0) {
        setDays(Math.floor(t / MILLIS_IN_DAY));
        setHours(Math.floor((t % MILLIS_IN_DAY) / MILLIS_IN_HOUR));
        setMins(Math.floor((t % MILLIS_IN_HOUR) / MILLIS_IN_MINUTE));
        setSecs(Math.floor((t % MILLIS_IN_MINUTE) / 1000));
      }
    };

    useEffect(() => {
      updateCountdownComponents();
    }, []);

    useInterval(updateCountdownComponents, 1000);

    if (secs === null) return <div style={{ minHeight: '112px' }} />;

    return (
      <div
        className={`nextWinnerBanner${className ? ` ${className}` : ''}`}
        style={style}
      >
        <div className="nextWinnerBannerContentContainer">
          <div className="timerSection">
            <p className="spacedFont heading">{heading}</p>

            <div className="countdownClock">
              {showDays && (
                <div className="clockComponent">
                  <h4>
                    {('0' + days)
                      .slice(-2)
                      .split('')
                      .map((digit, i) => (
                        <div
                          className={newStyle ? 'borderedBox' : ''}
                          key={i}
                        >
                          {digit}
                        </div>
                      ))}
                  </h4>
                  <p>Days</p>
                </div>
              )}

              <div className="clockComponent">
                <h4>
                  {('0' + hours)
                    .slice(-2)
                    .split('')
                    .map((digit, i) => (
                      <div
                        className={newStyle ? 'borderedBox' : ''}
                        key={i}
                      >
                        {digit}
                      </div>
                    ))}
                </h4>
                <p>Hrs</p>
              </div>

              <div className="clockComponent">
                <h4>
                  {('0' + mins)
                    .slice(-2)
                    .split('')
                    .map((digit, i) => (
                      <div
                        className={newStyle ? 'borderedBox' : ''}
                        key={i}
                      >
                        {digit}
                      </div>
                    ))}
                </h4>
                <p>Mins</p>
              </div>

              {showSeconds && (
                <div className="clockComponent">
                  <h4>
                    {('0' + secs)
                      .slice(-2)
                      .split('')
                      .map((digit, i) => (
                        <div
                          className={newStyle ? 'borderedBox' : ''}
                          key={i}
                        >
                          {digit}
                        </div>
                      ))}
                  </h4>
                  <p>Secs</p>
                </div>
              )}
            </div>
          </div>

          {withEnterDrawButton && (
            <RoundedButton href={getCta()?.url}>
              {window && window.matchMedia('(min-width: 350px)').matches
                ? 'Enter Now'
                : 'Enter'}
            </RoundedButton>
          )}
        </div>

        <style jsx>{`
          .nextWinnerBanner {
            width: auto;
            background: ${newStyle
              ? 'transparent'
              : `linear-gradient(180deg, ${barTint} 0%, rgba(255,255,255,${
                  fixedToBottom ? 1 : 0.8
                }) 100%)`};
            z-index: 2;
            width: ${fixedToBottom ? '96%' : '100%'};
            ${fixedToBottom ? 'border-radius: 5px;' : ''}
            ${fixedToBottom ? 'position: fixed;' : ''}
          ${fixedToBottom ? 'left: calc(50% - min(200px, 48%));' : ''}
          ${fixedToBottom ? 'max-width: 400px;' : ''}
          ${fixedToBottom
              ? 'bottom: max( calc(1.4em + env(safe-area-inset-bottom)), 1.5em );'
              : ''}
          ${fixedToBottom
              ? 'box-shadow: 0 0 8px 5px rgba(55, 55, 55, 0.3);'
              : ''}
          color: ${newStyle ? 'white' : '#383838'};
            transition: 0.2s;
          }

          .nextWinnerBannerContentContainer {
            padding: 1em;
            display: flex;
            max-width: 450px;
            margin: 0 auto;
            justify-content: space-around;
            align-items: center;
          }

          .heading {
            margin: 0.5em;
            text-shadow: ${newStyle ? '1px 1px black' : 'green'};
          }

          .countdownClock {
            display: flex;
            justify-content: center;
          }

          .clockComponent {
            display: flex;
            flex-direction: column;
            margin: 0 0.5em;
            min-width: 40px;
          }

          .clockComponent h4 {
            margin: 0;
            color: #383838;
            text-align: center;
            display: flex;
            justify-content: center;
          }

          .borderedBox {
            background-color: ${darkMode ? colors.primaryBrandGray : 'white'};
            color: ${darkMode ? 'white' : 'inherit'};
            border-radius: 3px;
            margin: 0.1em;
            text-align: center;
            padding: 0.3em;
            letter-spacing: 0;
            min-width: 25px;
            font-weight: 700;
            text-shadow: none;
          }

          .clockComponent p {
            margin: 3px 0 0 0;
            font-size: 0.9em;
            font-weight: 700;
            text-shadow: ${newStyle && !darkMode ? '1px 1px black' : ''};
            text-align: center;
          }
        `}</style>
      </div>
    );
  }
);

export default NextWinnerBanner;
